// Mapbox Geocoding utilities
// Since Mapbox Search API returns coordinates directly, we can extract them from the feature

function parseMapboxContext(context) {
  let address = null;
  let city = null;
  let postal = null;
  let region = null;
  let country = null;

  if (!context) {
    return { address, city, postal, region, country };
  }

  for (const item of context) {
    const id = item.id.split('.')[0];
    if (id === 'address') {
      address = item.text;
    } else if (id === 'place' && !city) {
      city = item.text;
    } else if (id === 'region') {
      region = item.short_code?.split('-')[1] || item.text;
    } else if (id === 'postcode') {
      postal = item.text;
    } else if (id === 'country') {
      country = item.short_code;
    }
  }

  return { address, city, postal, region, country };
}

export function getLatLngFromMapboxFeature(feature) {
  if (!feature) {
    throw new Error('Feature is required');
  }

  // If feature already has coordinates (from our transformed data)
  if (feature.coordinates) {
    const [lat, lng] = feature.coordinates;

    // Use already-parsed values if available, otherwise parse from address_components
    let city = feature.city;
    let region = feature.region;
    let postal = feature.postal;
    let country = feature.country;
    let address = feature.address;

    // If values aren't already parsed, parse from address_components
    const missingCity = !city;
    const missingRegion = !region;
    const missingCountry = !country;
    if (
      (missingCity || missingRegion || missingCountry) &&
      feature.address_components
    ) {
      const parsed = parseMapboxContext(feature.address_components);
      city = city || parsed.city;
      region = region || parsed.region;
      postal = postal || parsed.postal;
      country = country || parsed.country;
      address = address || parsed.address;
    }

    return {
      lat,
      lng,
      address,
      city,
      postal,
      region,
      country,
      formattedAddress:
        feature.formatted_address || feature.description || null,
    };
  }

  // If it's a raw Mapbox feature (fallback for direct API usage)
  if (feature.geometry?.coordinates) {
    const [lng, lat] = feature.geometry.coordinates;
    const properties = feature.properties || {};
    const { place_name, context } = properties;
    const { address, city, postal, region, country } =
      parseMapboxContext(context);

    return {
      lat,
      lng,
      address,
      city,
      postal,
      region,
      country,
      formattedAddress: place_name,
    };
  }

  throw new Error('Invalid feature format');
}

// For backward compatibility - if we need to geocode an address string
export async function geocodeAddress(address) {
  const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

  if (!accessToken) {
    throw new Error('Mapbox access token is not configured');
  }

  const params = new URLSearchParams({
    access_token: accessToken,
    limit: 1,
  });

  const response = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      address,
    )}.json?${params.toString()}`,
  );

  if (!response.ok) {
    throw new Error(`Mapbox API error: ${response.status}`);
  }

  const data = await response.json();

  if (data.features && data.features.length > 0) {
    return getLatLngFromMapboxFeature(data.features[0]);
  }

  throw new Error('No results found');
}
